import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, Maximize2, ImageOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { ProgressiveImage } from "@/components/ProgressiveImage";
import { useTranslation } from "react-i18next";

interface GalleryImage {
  url: string;
  title?: string;
}

interface EnhancedImageGalleryProps {
  images: GalleryImage[];
  productName: string;
}

export const EnhancedImageGallery = ({ images, productName }: EnhancedImageGalleryProps) => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());

  const maxZoom = 3;
  const minZoom = 1;
  const zoomStep = 0.5;

  const goToPrevious = useCallback(() => {
    const validIndices = images.map((_, i) => i).filter(i => !failedImages.has(i));
    const currentValidIndex = validIndices.indexOf(selectedIndex);
    const newIndex = currentValidIndex === 0 
      ? validIndices[validIndices.length - 1] 
      : validIndices[currentValidIndex - 1];
    setSelectedIndex(newIndex);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, [images.length, failedImages, selectedIndex]);

  const goToNext = useCallback(() => {
    const validIndices = images.map((_, i) => i).filter(i => !failedImages.has(i));
    const currentValidIndex = validIndices.indexOf(selectedIndex);
    const newIndex = currentValidIndex === validIndices.length - 1
      ? validIndices[0]
      : validIndices[currentValidIndex + 1];
    setSelectedIndex(newIndex);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, [images.length, failedImages, selectedIndex]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + zoomStep, maxZoom));
  };

  const handleZoomOut = () => {
    setZoom((prev) => {
      const newZoom = Math.max(prev - zoomStep, minZoom);
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowLeft":
          goToPrevious();
          break;
        case "ArrowRight":
          goToNext();
          break;
        case "Escape":
          setIsFullscreen(false);
          break;
        case "+":
        case "=":
          handleZoomIn();
          break;
        case "-":
          handleZoomOut();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isFullscreen, goToPrevious, goToNext]);

  const handleImageError = (index: number) => {
    setFailedImages(prev => new Set(prev).add(index));
  };

  // Filter valid images
  const validImages = images.filter((_, index) => !failedImages.has(index));
  const allImagesFailed = images.length > 0 && validImages.length === 0;

  // Adjust selected index if current image failed
  useEffect(() => {
    if (failedImages.has(selectedIndex) && validImages.length > 0) {
      const validIndices = images.map((_, i) => i).filter(i => !failedImages.has(i));
      setSelectedIndex(validIndices[0] || 0);
    }
  }, [failedImages, selectedIndex, validImages.length, images]);

  if (!images || images.length === 0 || allImagesFailed) {
    return (
      <div className="relative rounded-xl overflow-hidden mb-4 bg-gradient-to-br from-muted via-muted/80 to-muted/50 backdrop-blur-sm h-[400px] lg:h-[500px] flex flex-col items-center justify-center">
        <ImageOff className="h-20 w-20 mb-4 text-muted-foreground/30" />
        <p className="text-muted-foreground text-lg">{t('imageGallery.noImages')}</p>
      </div>
    );
  }

  const currentImage = validImages[selectedIndex] || images[0];

  return (
    <>
      {/* Main Gallery Grid */}
      <div className="grid grid-cols-4 gap-2 rounded-lg overflow-hidden">
        {/* Large main image */}
        {!failedImages.has(0) ? (
          <div
            className="col-span-4 md:col-span-2 row-span-2 relative group cursor-pointer overflow-hidden bg-muted"
            onClick={() => {
              setSelectedIndex(0);
              setIsFullscreen(true);
            }}
            style={{ minHeight: "400px", maxHeight: "500px" }}
          >
            <ProgressiveImage
              src={images[0].url}
              alt={images[0].title || `${productName} - ${t('imageGallery.mainImage')}`}
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              onError={() => handleImageError(0)}
              loading="eager"
              fetchPriority="high"
              layout="hero"
              sizes="(max-width: 768px) 100vw, 50vw"
              rootMargin="0px"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <Maximize2 className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        ) : null}

        {/* Smaller images */}
        {images.slice(1, 5).map((image, index) => {
          const imageIndex = index + 1;
          if (failedImages.has(imageIndex)) return null;
          
          return (
            <div
              key={imageIndex}
              className="relative group cursor-pointer overflow-hidden bg-muted"
              onClick={() => {
                setSelectedIndex(imageIndex);
                setIsFullscreen(true);
              }}
              style={{ minHeight: "120px", maxHeight: "200px" }}
            >
              <ProgressiveImage
                src={image.url}
                alt={image.title || `${productName} - ${t('imageGallery.imageNumber', { number: imageIndex + 1 })}`}
                className="object-cover group-hover:scale-105 transition-transform duration-300"
                onError={() => handleImageError(imageIndex)}
                loading="lazy"
                layout="smallCard"
                sizes="(max-width: 768px) 50vw, 25vw"
                rootMargin="200px"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <Maximize2 className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              {index === 3 && images.length > 5 && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="text-white text-lg font-semibold">
                    {t('imageGallery.morePhotos', { count: images.length - 5 })}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Fullscreen Modal */}
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-screen-2xl h-screen p-0 bg-black/95">
          <DialogHeader className="sr-only">
            <DialogTitle>{t('imageGallery.galleryTitle')}</DialogTitle>
            <DialogDescription>{productName}</DialogDescription>
          </DialogHeader>
          <div className="relative w-full h-full flex flex-col">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
              <div className="flex items-center justify-between text-white">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">
                    {currentImage.title || productName}
                  </h3>
                  <p className="text-sm text-white/70">
                    {selectedIndex + 1} / {images.length}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsFullscreen(false)}
                  className="text-white hover:bg-white/20"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </div>

            {/* Main Image */}
            <div
              className="flex-1 flex items-center justify-center relative overflow-hidden"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{ cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default" }}
            >
              <img
                src={currentImage.url}
                alt={currentImage.title || `${productName} - ${t('imageGallery.imageNumber', { number: selectedIndex + 1 })}`}
                className="max-w-full max-h-full object-contain transition-transform duration-200"
                style={{
                  transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                }}
                draggable={false}
                onError={() => handleImageError(selectedIndex)}
              />
            </div>

            {/* Controls */}
            <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent p-4">
              <div className="flex items-center justify-center gap-4">
                {/* Zoom Controls */}
                <div className="flex items-center gap-2 bg-black/60 rounded-lg p-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleZoomOut}
                    disabled={zoom <= minZoom}
                    className="text-white hover:bg-white/20"
                  >
                    <ZoomOut className="h-5 w-5" />
                  </Button>
                  <span className="text-white text-sm min-w-[60px] text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleZoomIn}
                    disabled={zoom >= maxZoom}
                    className="text-white hover:bg-white/20"
                  >
                    <ZoomIn className="h-5 w-5" />
                  </Button>
                </div>

                {/* Navigation Controls */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={goToPrevious}
                    className="text-white hover:bg-white/20"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={goToNext}
                    className="text-white hover:bg-white/20"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                </div>
              </div>

              {/* Thumbnail Strip */}
              <div className="mt-4 flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedIndex(index);
                      setZoom(1);
                      setPosition({ x: 0, y: 0 });
                    }}
                    className={cn(
                      "flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden transition-all",
                      index === selectedIndex
                        ? "ring-2 ring-white scale-110"
                        : "opacity-60 hover:opacity-100"
                    )}
                  >
                    <img
                      src={image.url}
                      alt={image.title || t('imageGallery.thumbnail', { index: index + 1 })}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation Arrows (on sides) */}
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 w-12 h-12"
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 w-12 h-12"
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
